// src/steps/Step3FutureRoles.js
import { AssistantMark, AssistantNote } from "../components/common/AssistantFields";
import React, { useState } from "react";
import {
  Box,
  Chip,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Paper,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WordCountTextFieldWithExample from "../components/WordCountTextFieldWithExample";

// Example text for expected contribution (from fillDummyValues)
const EXPECTED_CONTRIBUTION_EXAMPLE = `
The learner(s) help(s) colleagues understand what LDT models can and cannot say, and co-design simple explorable views for non-technical staff; document assumptions, highlight data gaps, and support workshops.
`.trim();

// Guiding template text matching STEP 3 structure comment
const EXPECTED_CONTRIBUTION_GUIDING_TEXT = `
Describe the expected contribution: What are the learner(s)' new tasks, behaviours and responsibilities?
`.trim();

export default function Step3FutureRoles({
  // roleOptions is ignored for layout/colors; roles are defined here
  futureRoles,
  setFutureRoles,
  customRoleInput,
  setCustomRoleInput,
  expectedContribution,
  setExpectedContribution,
  onSave, // kept for global Save Step 3 (in App), not used locally
  sessionId, // only used by App-level logic
}) {
  const MAX_ROLES = 4;
  const rolesCount = futureRoles.length;
  const hasReachedMaxRoles = rolesCount >= MAX_ROLES;

  // ---- Framework colors ----
  const COLORS = {
    learn: "#D32F2F", // red
    trust: "#FBC02D", // yellow / sand
    sustain: "#388E3C", // green
    build: "#1976D2", // blue
    custom: "#9E9E9E", // grey
  };

  // ---- New 12 roles grouped by framework (with colors) ----
  const defaultRoles = [
    // LEARN (Red)
    {
      label: "Enabler of Data and Modeling Literacy",
      color: COLORS.learn,
      framework: "learn",
    },
    {
      label: "Explorer of New Methods and Innovation Pathways",
      color: COLORS.learn,
      framework: "learn",
    },
    {
      label: "Facilitator of Co-Creative Learning Processes",
      color: COLORS.learn,
      framework: "learn",
    },
    // TRUST (Yellow)
    {
      label: "Guardian of Digital Responsibility",
      color: COLORS.trust,
      framework: "trust",
    },
    {
      label: "Advocate for Inclusive Participation",
      color: COLORS.trust,
      framework: "trust",
    },
    {
      label: "Translator Between the Public and the Model World",
      color: COLORS.trust,
      framework: "trust",
    },
    // SUSTAIN (Green)
    {
      label: "Connector of Urban and Regional Systems",
      color: COLORS.sustain,
      framework: "sustain",
    },
    {
      label: "Designer of Climate Adaptation and Resilience",
      color: COLORS.sustain,
      framework: "sustain",
    },
    {
      label: "Pathfinder for Governance-Ready Insights",
      color: COLORS.sustain,
      framework: "sustain",
    },
    // BUILD (Blue)
    {
      label: "Designer of the Digital Twin Infrastructure",
      color: COLORS.build,
      framework: "build",
    },
    {
      label: "Modeler of Spatial and Dynamic Processes",
      color: COLORS.build,
      framework: "build",
    },
    {
      label: "Responsible Lead for Data Quality and Interoperability",
      color: COLORS.build,
      framework: "build",
    },
  ];

  // Custom roles (chips) live here (grey)
  const [customRoles, setCustomRoles] = useState([]);

  const toggleRole = (roleLabel) => {
    setFutureRoles((prev) => {
      const alreadySelected = prev.includes(roleLabel);
      if (alreadySelected) {
        // Always allow deselecting a role
        return prev.filter((r) => r !== roleLabel);
      }
      // Prevent selecting more than MAX_ROLES
      if (prev.length >= MAX_ROLES) {
        return prev;
      }
      return [...prev, roleLabel];
    });
  };

  // Add custom role as a grey-outlined chip
  const handleCustomRoleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If we already have 4 roles, do not allow adding custom roles
      if (hasReachedMaxRoles) {
        return;
      }
      const v = customRoleInput.trim();
      if (!v) return;
      if (!customRoles.some((r) => r.label === v)) {
        setCustomRoles((prev) => [
          ...prev,
          { label: v, color: COLORS.custom },
        ]);
      }
      if (!futureRoles.includes(v)) {
        setFutureRoles((prev) => [...prev, v]);
      }
      setCustomRoleInput("");
    }
  };

  // For the 3x4 grid, just use defaultRoles in framework order: 3 per row
  const frameworkOrder = ["learn", "trust", "sustain", "build"];

  // Shared Chip styling to allow multi-line labels and stretch to row height
  const chipMultiLineSx = (color, isCustom = false) => ({
    width: "100%",
    height: "100%",
    borderColor: color,
    borderWidth: 2,
    borderStyle: isCustom ? "dashed" : "solid", // subtle distinction for custom
    justifyContent: "flex-start",
    alignItems: "flex-start",
    "& .MuiChip-label": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "unset",
      display: "block",
      textAlign: "left",
      lineHeight: 1.3,
      paddingTop: "4px",
      paddingBottom: "4px",
      // long words (e.g. "Interoperability") must wrap inside narrow phone cards
      hyphens: "auto",
      WebkitHyphens: "auto",
      overflowWrap: "break-word",
      fontSize: { xs: "0.72rem", sm: "0.8125rem" },
      paddingLeft: { xs: "5px", sm: "12px" },
      paddingRight: { xs: "5px", sm: "12px" },
      fontStyle: isCustom ? "italic" : "normal",
    },
  });

  return (
    <Box>
      {/* ---- Title + Info Tooltip ---- */}
      <Box display="flex" alignItems="center" mb={1.5}>
        <Typography variant="h6" sx={{ mr: 1 }}>
          Step 3 · Future Role & Contribution
        </Typography>
        <Tooltip
          title={
            <Box>
              <Typography sx={{ fontSize: 16, mb: 1, lineHeight: 1.5 }}>
                The Co-Agency Model shows how learners and organisations grow
                together when bringing these four roles into practice: Learn,
                Trust, Sustain, Build. Learning contributes to this Co-Agency
                Process.
              </Typography>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/TtT-co-agency.jpg`}
                alt="Co-Agency Model"
                sx={{
                  width: "100%",
                  maxWidth: 350,
                  borderRadius: 1,
                  mt: 1,
                  display: "block",
                  mx: "auto",
                }}
              />
            </Box>
          }
          componentsProps={{
            tooltip: { sx: { maxWidth: 420, padding: 2 } },
          }}
        >
          <IconButton size="small" sx={{ color: "black", p: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Explanatory text under the heading */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The T4R Co-Agency circle focuses on an understanding of interlocked
        roles (Learn, Trust, Sustain, Build) necessary for an LDT development.
        Here you find several fictional roles. <strong>Select up to four
        roles</strong> to describe how the learner(s) should contribute to the
        LDT project.
      </Typography>

      {/* ---- Roles selection card ---- */}
      <Paper
        variant="outlined"
        sx={{
          mt: 0,
          mb: 1,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <AssistantNote field="future_roles" />
        {/* Default roles grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: { xs: 1, sm: 1.5 },
            mb: 2,
          }}
        >
          {frameworkOrder.flatMap((fw) =>
            defaultRoles
              .filter((r) => r.framework === fw)
              .map((role) => {
                const selected = futureRoles.includes(role.label);
                return (
                  <Box
                    key={role.label}
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    <Chip
                      label={role.label}
                      variant={selected ? "filled" : "outlined"}
                      color={selected ? "primary" : "default"}
                      onClick={() => toggleRole(role.label)}
                      sx={chipMultiLineSx(role.color)}
                    />
                  </Box>
                );
              })
          )}
        </Box>

        {/* Custom roles grid inside the same card */}
        {customRoles.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Custom roles you added
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 1,
              }}
            >
              {customRoles.map((role) => {
                const selected = futureRoles.includes(role.label);
                return (
                  <Box
                    key={role.label}
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    <Chip
                      label={role.label}
                      variant={selected ? "filled" : "outlined"}
                      color={selected ? "primary" : "default"}
                      onClick={() => toggleRole(role.label)}
                      sx={chipMultiLineSx(role.color, true)}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Custom role input, still part of the card */}
        <Box sx={{ mt: 3 }}>
          <TextField
            label="Add other roles if needed and press Enter (500 characters)"
            fullWidth
            value={customRoleInput}
            onChange={(e) => {
              const next = e.target.value ?? "";
              if (next.length <= 500) {
                setCustomRoleInput(next);
              }
            }}
            onKeyDown={handleCustomRoleKeyDown}
            disabled={hasReachedMaxRoles}
            inputProps={{ maxLength: 500 }}
          />
        </Box>
      </Paper>

      {/* ---- Bottom status line for roles (below the card, outside) ---- */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 1, mb: 3 }}
      >
        {/* Left: max message (only when reached) */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minHeight: "1em" }} // keeps layout stable
        >
          {hasReachedMaxRoles
            ? "Maximum reached — remove one role to choose another."
            : ""}
        </Typography>
        {/* Right: N / 4 roles selected */}
        <Typography variant="caption" color="text.secondary">
          {rolesCount} / {MAX_ROLES} roles selected
        </Typography>
      </Box>

      {/* ---- Expected Contribution (WordCountTextFieldWithExample) ---- */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: "rgba(0, 0, 0, 0.02)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Expected contribution
        </Typography>

        <AssistantMark field="expected_contribution">

        <WordCountTextFieldWithExample
          // label removed to avoid redundancy with the section header
          value={expectedContribution}
          onChange={(val) => setExpectedContribution(val)}
          minRows={5}
          minWords={40}
          maxWords={100}
          warnOverBy={10}
          maxCharsPerWord={45}
          placeholder={
            "Click on the 'Show guiding questions' button to see a template for describing the expected contribution"
          }
          exampleText={EXPECTED_CONTRIBUTION_EXAMPLE}
          exampleTitle="Example expected contribution"
          guidingText={EXPECTED_CONTRIBUTION_GUIDING_TEXT}
          guidingTitle="Template for describing the expected contribution"
        />

        </AssistantMark>
      </Box>
    </Box>
  );
}
